import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { money, moneyIn } from "../lib/api";
import { StatusChip } from "./shared";
import { DataTable } from "./DataTable";

const fmt = (n, cur) => (!cur || cur.toUpperCase() === "AUD" ? money(n) : moneyIn(n, cur));
// Purchase invoices repeat what was received, so they're left out of the quantity total.
const countsInTotal = (r) => r.type !== "Purchase Invoice";

const TABS = [["sales", "Sales"], ["purchases", "Purchases"], ["quotes", "Quotes"]];

const cols = (partyLabel) => [
  { key: "date", header: "Date", cellClassName: "font-mono text-xs" },
  { key: "type", header: "Type", filter: "select", options: [], cellClassName: "text-xs text-muted-foreground" },
  { key: "doc_no", header: "Document", filter: "text", cellClassName: "font-mono text-xs font-medium text-primary" },
  { key: "party", header: partyLabel, filter: "text", cellClassName: "text-sm" },
  { key: "quantity", header: "Qty", align: "right", numeric: true, cellClassName: "tabular text-sm" },
  { key: "unit_price", header: "Unit Price", align: "right", numeric: true, cell: (r) => <span className="tabular text-sm">{fmt(r.unit_price, r.currency)}</span> },
  { key: "amount", header: "Amount", align: "right", numeric: true, cell: (r) => <span className="tabular text-sm font-medium">{fmt(r.amount, r.currency)}</span> },
  { key: "status", header: "Status", filter: "select", options: [], cell: (r) => <StatusChip status={r.status || "Open"} /> },
];

export const ItemHistory = ({ itemId }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("sales");
  const { data, isLoading } = useQuery({ queryKey: ["item-history", itemId], queryFn: async () => (await api.get(`/items/${itemId}/history`)).data });
  const rows = data?.[tab] || [];
  const withOpts = cols(tab === "purchases" ? "Vendor" : "Customer").map((c) => (c.filter === "select" ? { ...c, options: [...new Set(rows.map((r) => r[c.key]).filter(Boolean))] } : c));
  const totalOf = (list) => Math.round(list.filter(countsInTotal).reduce((s, r) => s + Number(r.quantity || 0), 0) * 100) / 100;
  return (
    <div className="border border-border bg-card mt-5" data-testid="item-history">
      <div className="flex items-center gap-1 px-3 pt-3 border-b border-border">
        <span className="font-bold text-sm mr-3">History</span>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`item-history-tab-${k}`}
            className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label} <span className="ml-1 tabular text-muted-foreground">{(data?.[k] || []).length}</span>
          </button>
        ))}
      </div>
      <DataTable data={rows} columns={withOpts} rowKey={(r) => `${r.type}-${r.id}`} rowTestid="item-history-row" testid="item-history-table"
        toolbarExtra={(view) => <span className="text-xs text-muted-foreground" data-testid="item-history-total-qty">Total qty{tab === "purchases" ? " (orders + receipts)" : ""}: <b className="text-foreground tabular">{totalOf(view)}</b></span>}
        loading={isLoading} empty={tab === "quotes" ? "This item hasn't been quoted yet." : "No history for this item yet."}
        rowProps={(r) => ({ onClick: () => navigate(r.link), className: "cursor-pointer" })} />
    </div>
  );
};
